import { useState } from 'react';
import {
  getDefaultEmailTemplate,
  renderPlaceholdersInTemplates
} from 'shared-helpers/email';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import { AccessRequests } from 'components/common/accessRequest';
import { PathsInProduct } from 'components/common/pathsInContent';
import { ShareGroups, ShareUsers } from 'components/courses/share';

import type { User } from 'shared-types';
import type {
  GetCourseUsersRes,
  GetEmailTemplatesByTypeRes
} from 'shared-types/responses';

export type UpdateShareObject = {
  page?: number;
  search?: string;
  sortBy?: string;
};

export type ShareUser = User &
  Pick<
    GetCourseUsersRes['users'][number],
    | 'addedDate'
    | 'deadline'
    | 'inviteEmailDate'
    | 'courseSettings'
    | 'mandatoryReasons'
  >;

const Share = () => {
  const {
    language,
    pageState: { course },
    serverConfig: { adminUrl, userUseName, hidePathsFromUI },
    user
  } = useReduxState();

  const [usersPage, setUsersPage] = useState(1);
  const [usersSearch, setUsersSearch] = useState('');
  const [usersSortBy, setUsersSortBy] = useState(
    userUseName ? 'name' : 'username'
  );

  // get users
  const {
    data: usersData,
    loading: usersDataLoading,
    updateData: updateUsersData
  } = useApiData<GetCourseUsersRes>({
    url:
      `/admin/courses/${course.id}/users?page=${usersPage}&sortBy=${usersSortBy}` +
      (usersSearch ? `&search=${usersSearch}` : '')
  });

  const listOfUserIds = (usersData?.listOfUserIds || []) as number[];
  const users = (usersData?.users || []) as ShareUser[];

  // get email templates
  const { data: emailTemplatesData } = useApiData<GetEmailTemplatesByTypeRes>({
    url: `/admin/email-templates-data?type=course-access&courseId=${course.id}`
  });

  let emailTemplates = emailTemplatesData?.emailTemplates || [];

  emailTemplates = renderPlaceholdersInTemplates({
    emailTemplates,
    overridePlaceholderValues: {
      contactEmail: user?.email,
      courseTitle: course.title,
      courseUrl: `${adminUrl}/courses/${course.slug}`
    }
  });

  const emailTemplatesDefault = getDefaultEmailTemplate({
    language,
    emailTemplates
  });

  const handleRequestAction = async (action: 'accept' | 'deny') => {
    if (action === 'accept') await updateUsersData();
  };

  const updateUsersParams = ({
    page,
    search,
    sortBy
  }: {
    page?: number;
    search?: string;
    sortBy?: string;
  }) => {
    if (page !== undefined) setUsersPage(page);
    if (search !== undefined) setUsersSearch(search);
    if (sortBy !== undefined) setUsersSortBy(sortBy);
  };

  return (
    <>
      {course?.userAccess === 'request-access' ? (
        <div style={{ marginBottom: 40 }}>
          <AccessRequests
            onRequestAction={handleRequestAction}
            url={`/admin/courses/${course.id}/request-access`}
          />
        </div>
      ) : null}

      <div>
        <ShareUsers
          emailTemplates={emailTemplates}
          emailTemplatesDefault={emailTemplatesDefault}
          listOfUserIds={listOfUserIds}
          loading={usersDataLoading}
          page={usersPage}
          pages={usersData?.pages}
          sortBy={usersSortBy}
          updateUsers={updateUsersData}
          updateUsersParams={updateUsersParams}
          users={users}
        />
      </div>

      <div style={{ marginTop: 40 }}>
        <ShareGroups
          emailTemplates={emailTemplates}
          emailTemplatesDefault={emailTemplatesDefault}
          updateUsers={updateUsersData}
        />
      </div>

      {!hidePathsFromUI && (
        <div style={{ marginTop: 40 }}>
          <PathsInProduct
            guestOnly={course.userAccess === 'guests'}
            url={`/admin/courses/${course.id}/paths`}
          />
        </div>
      )}
    </>
  );
};

export default Share;
